import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { FaClock, FaArrowUp, FaEnvelope, FaChartLine, FaCalendarPlus } from 'react-icons/fa';
import './TrialManagementDashboard.css';

function TrialManagementDashboard() {
  const [trials, setTrials] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extensionDays, setExtensionDays] = useState(7);

  useEffect(() => {
    fetchTrialData();
  }, []);

  const fetchTrialData = async () => {
    try {
      setLoading(true);
      
      // Fetch active trials
      const trialsResponse = await apiClient.get('/admin/trials/active');
      setTrials(trialsResponse.data);
      
      // Fetch trial metrics
      const metricsResponse = await apiClient.get('/admin/trials/metrics');
      setMetrics(metricsResponse.data);
      
    } catch (error) {
      console.error('Error fetching trial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExtendTrial = async () => {
    if (!selectedTrial) return;
    
    try {
      await apiClient.post(`/admin/trials/extend/${selectedTrial.id}`, {
        additional_days: extensionDays
      });
      
      setShowExtendModal(false);
      setSelectedTrial(null);
      fetchTrialData(); // Refresh data
    } catch (error) {
      console.error('Error extending trial:', error);
    }
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getStatusClass = (daysRemaining) => {
    if (daysRemaining <= 3) return 'critical';
    if (daysRemaining <= 7) return 'warning';
    return 'normal';
  };

  if (loading) {
    return <div className="loading">Loading trial data...</div>;
  }

  return (
    <div className="trial-management-dashboard">
      <div className="dashboard-header">
        <h1>Trial Management Dashboard</h1>
        <p>Monitor and manage all active trials</p>
      </div>

      {/* Metrics Summary */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon">
            <FaClock />
          </div>
          <div className="metric-content">
            <h3>Active Trials</h3>
            <div className="metric-value">{metrics.active_trials || 0}</div>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon">
            <FaArrowUp />
          </div>
          <div className="metric-content">
            <h3>Conversion Rate</h3>
            <div className="metric-value">{metrics.conversion_rate || 0}%</div>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon">
            <FaChartLine />
          </div>
          <div className="metric-content">
            <h3>Avg Days to Convert</h3>
            <div className="metric-value">{metrics.avg_days_to_convert || 0}</div>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon">
            <FaCalendarPlus />
          </div>
          <div className="metric-content">
            <h3>This Month</h3>
            <div className="metric-value">{metrics.trials_this_month || 0}</div>
          </div>
        </div>
      </div>

      {/* Active Trials Table */}
      <div className="trials-section">
        <h2>Active Trials</h2>
        <div className="trials-table">
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th>Plan</th>
                <th>Started</th>
                <th>Ends</th>
                <th>Days Left</th>
                <th>Usage</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {trials.map(trial => {
                const daysRemaining = getDaysRemaining(trial.trial_end_date);
                const statusClass = getStatusClass(daysRemaining);
                
                return (
                  <tr key={trial.id} className={statusClass}>
                    <td>
                      <div className="company-info">
                        <div className="company-name">{trial.company_name}</div>
                        <div className="company-email">{trial.primary_contact}</div>
                      </div>
                    </td>
                    <td>{trial.plan_name}</td>
                    <td>{new Date(trial.start_date).toLocaleDateString()}</td>
                    <td>{new Date(trial.trial_end_date).toLocaleDateString()}</td>
                    <td>
                      <span className={`days-badge ${statusClass}`}>
                        {daysRemaining} days
                      </span>
                    </td>
                    <td>
                      <div className="usage-stats">
                        <div>Employees: {trial.usage?.employees_added || 0}</div>
                        <div>Projects: {trial.usage?.projects_created || 0}</div>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="action-btn extend"
                          onClick={() => {
                            setSelectedTrial(trial);
                            setShowExtendModal(true);
                          }}
                          title="Extend Trial"
                        >
                          <FaCalendarPlus />
                        </button>
                        <button 
                          className="action-btn contact"
                          onClick={() => window.location.href = `mailto:${trial.primary_contact}`}
                          title="Contact Company"
                        >
                          <FaEnvelope />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {trials.length === 0 && (
            <div className="no-trials">
              No active trials at the moment
            </div>
          )}
        </div>
      </div>

      {/* Extend Trial Modal */}
      {showExtendModal && (
        <div className="modal-overlay" onClick={() => setShowExtendModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Extend Trial</h3>
            <p>Extending trial for: <strong>{selectedTrial?.company_name}</strong></p>
            
            <div className="form-group">
              <label>Additional Days:</label>
              <select 
                value={extensionDays} 
                onChange={(e) => setExtensionDays(Number(e.target.value))}
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={() => setShowExtendModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-extend" 
                onClick={handleExtendTrial}
              >
                Extend Trial
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrialManagementDashboard;